import { PaginationMetadata, type ApiPaginationMetadata } from '@/types/api';
import { type TeamworksAppSlug } from '@teamworksdev/vuetify';

export const TEAMWORKS_APP_STATUS_ENUM = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

export type TeamworksAppStatus = typeof TEAMWORKS_APP_STATUS_ENUM[keyof typeof TEAMWORKS_APP_STATUS_ENUM];

export interface ApiAxleProfile {
  is_collegiate: boolean | null;
  login_access: Record<string, Record<TeamworksAppSlug, boolean>>;
  name: string | null;
  navbar_bottom_color: string | null;
  navbar_image_url: string | null;
  navbar_top_color: string | null;
  org_document_id: string;
  person_document_id: string;
  target_urls: {
    slug: TeamworksAppSlug;
    url: string;
  }[];
  apps: {
    [key in TeamworksAppSlug]?: {
      has_active_profile: boolean;
    };
  };
}

export class AxleProfile {
  appTargetUrls: ApiAxleProfile['target_urls'];
  loginAccess: Record<TeamworksAppSlug, boolean>;
  navbarBottomColor: string | null;
  navbarImageUrl: string | null;
  navbarTopColor: string | null;
  orgTwrn: string;
  userTwrn: string;
  accountApps: {
    [key in TeamworksAppSlug]?: {
      hasActiveProfile: boolean;
    };
  };

  constructor(data: ApiAxleProfile) {
    this.appTargetUrls = data.target_urls;
    this.loginAccess = data.login_access['web'] as Record<TeamworksAppSlug, boolean>;
    this.navbarBottomColor = data.navbar_bottom_color;
    this.navbarImageUrl = data.navbar_image_url;
    this.navbarTopColor = data.navbar_top_color;
    this.orgTwrn = data.org_document_id;
    this.userTwrn = data.person_document_id;
    this.accountApps = data.apps
      ? Object.fromEntries(
          Object.entries(data.apps).map(([key, value]) => [key, { hasActiveProfile: value.has_active_profile }]),
        )
      : {};
  }
}

export interface ApiAxleToken {
  token: string;
}

export interface ApiLinkableProfile {
  twrn: string;
  gm_twrn: string | null;
  first_name: string;
  last_name: string;
  preferred_name: string;
  email: string;
  status: TeamworksAppStatus;
  profile_photo_url: string | null;
  apps: {
    [app: string]: {
      slug: TeamworksAppSlug;
      status: TeamworksAppStatus;
    };
  };
};

export class LinkableProfile {
  twrn: string;
  gmTwrn: string | null;
  firstName: string;
  lastName: string;
  preferredName: string;
  fullName: string;
  email: string;
  status: TeamworksAppStatus;
  profilePhotoUrl: string | null;
  apps: {
    [app: string]: {
      slug: TeamworksAppSlug;
      status: TeamworksAppStatus;
    };
  };

  constructor(data: ApiLinkableProfile) {
    this.twrn = data.twrn;
    this.gmTwrn = data.gm_twrn;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.preferredName = data.preferred_name;
    this.fullName = `${this.preferredName ?? this.firstName} ${this.lastName}`;
    this.email = data.email;
    this.status = data.status;
    this.profilePhotoUrl = data.profile_photo_url;
    this.apps = data.apps;
  }
}

export interface ApiLinkableProfilesResponse {
  data: ApiLinkableProfile[];
  metadata: ApiPaginationMetadata;
}

export class LinkableProfilesResponse {
  data: LinkableProfile[];
  metadata: PaginationMetadata;

  constructor(data: ApiLinkableProfilesResponse) {
    this.data = data.data.map((profile) => new LinkableProfile(profile));
    this.metadata = new PaginationMetadata(data.metadata);
  }
}
